// "Documentos Escaneados": everything the documents mailbox received
// (car-docs-inbox service), linked or not. Lets a person check, fix, link,
// unlink or ignore each document.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  Tabs,
  IndexTable,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  InlineGrid,
  Button,
  Modal,
  TextField,
  Select,
  Spinner,
  Pagination,
  Banner,
  Box,
  Divider,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatFriendlyDate } from "../../utility/funcs";
import {
  DOC_TYPE_LABELS,
  ENTITY_LABELS,
  MATCH_LABELS,
  SLOTS,
  defaultEntityType,
  defaultSlot,
  deleteDocument,
  entityLabels,
  entityRoute,
  linkDocument,
  nameMismatch,
  restoreOriginal,
  saveEdit,
  searchEntities,
  signedUrl,
  unlinkDocument,
} from "./scannedDocs";
import ImageEditor from "./ImageEditor";

const TABS = [
  { id: "unlinked", content: "Sin vincular" },
  { id: "linked", content: "Vinculados" },
  { id: "ignored", content: "Ignorados" },
  { id: "all", content: "Todos" },
];
const PAGE_SIZE = 25;
const FIELD_LABELS = {
  vin: "VIN", placa: "Placa", marca: "Marca", linea: "Línea", modelo: "Modelo", fecha: "Fecha",
  vigencia: "Vigencia", folio: "Folio", nombre: "Nombre", curp: "CURP", rfc: "RFC",
  razon_social: "Razón social", numero_licencia: "No. licencia", clave_elector: "Clave de elector",
  cic: "CIC", legible: "Legible",
};

function StatusBadge({ status, mismatch }) {
  if (status === "linked") return <Badge tone={mismatch ? "critical" : "success"}>Vinculado</Badge>;
  if (status === "ignored") return <Badge>Ignorado</Badge>;
  return <Badge tone="attention">Sin vincular</Badge>;
}

// ICVNL receipts are invoiced automatically through facturador-svc (car-docs-inbox).
function InvoiceBadge({ doc }) {
  if (doc.doc_type !== "recibo_icvnl" || !doc.invoice_status) return null;
  const s = doc.invoice_status;
  if (s === "done" || s === "issued") return <Badge tone="success">Facturado</Badge>;
  if (["needs_attention", "failed", "unsupported", "submit_error"].includes(s))
    return <Badge tone="critical">Factura: revisar</Badge>;
  if (s === "cancelled") return <Badge>Factura cancelada</Badge>;
  return <Badge tone="info">Factura pendiente</Badge>;
}

function ScannedDocuments() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const userId = session?.user?.id;

  const [tab, setTab] = useState(0);
  const [counts, setCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadCounts = useCallback(async () => {
    const { data } = await supabase.rpc("scanned_documents_counts");
    const c = {};
    (data || []).forEach((r) => (c[r.status] = Number(r.n)));
    c.all = Object.values(c).reduce((a, b) => a + b, 0);
    setCounts(c);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const status = TABS[tab].id;
    let q = supabase
      .from("scanned_documents")
      .select("*")
      .order("created_at", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    if (status !== "all") q = q.eq("status", status);
    const term = search.trim();
    if (term) {
      // Free text over what was read (CURP, VIN, plate, name...) and where it came from.
      const like = `%${term}%`;
      q = q.or(
        `summary.ilike.${like},original_filename.ilike.${like},email_from.ilike.${like},extracted->>curp.ilike.${like},extracted->>vin.ilike.${like},extracted->>placa.ilike.${like},extracted->>nombre.ilike.${like},extracted->>rfc.ilike.${like}`
      );
    }
    const { data, error } = await q;
    if (error) console.error(error);
    const list = data || [];
    setHasMore(list.length > PAGE_SIZE);
    const visible = list.slice(0, PAGE_SIZE);
    setRows(visible);
    setLabels(await entityLabels(visible));
    setLoading(false);
  }, [tab, page, search]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const refresh = async (keepSelectedId) => {
    await Promise.all([load(), loadCounts()]);
    if (keepSelectedId) {
      const { data } = await supabase.from("scanned_documents").select("*").eq("id", keepSelectedId).single();
      setSelected(data || null);
    }
  };

  const tabs = TABS.map((t) => ({
    ...t,
    content: counts[t.id] != null ? `${t.content} (${counts[t.id]})` : t.content,
  }));

  const linkedTo = (r) =>
    r.status === "linked" && r.entity_type
      ? `${ENTITY_LABELS[r.entity_type]}: ${labels[`${r.entity_type}:${r.entity_id}`] || r.entity_id}`
      : r.suggested_label
      ? `Sugerido: ${r.suggested_label}`
      : "";

  const rowMarkup = rows.map((r, i) => (
    <IndexTable.Row id={String(r.id)} key={r.id} position={i} onClick={() => setSelected(r)}>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold">
          {DOC_TYPE_LABELS[r.doc_type] || r.doc_type}
        </Text>
        <Text tone="subdued" variant="bodySm">
          {r.summary}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <BlockStack gap="100" inlineAlign="start">
          <StatusBadge status={r.status} mismatch={nameMismatch(r, labels[`${r.entity_type}:${r.entity_id}`])} />
          <InvoiceBadge doc={r} />
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text>{linkedTo(r)}</Text>
        {r.status === "linked" && r.match_method && (
          <Text tone="subdued" variant="bodySm">
            {r.linked_by ? "Manual" : `Automático · ${MATCH_LABELS[r.match_method] || r.match_method}`}
          </Text>
        )}
        {r.status === "unlinked" && r.match_note && (
          <Text tone="subdued" variant="bodySm">
            {r.match_note}
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm">{r.email_from || r.source}</Text>
        <Text tone="subdued" variant="bodySm">
          {r.original_filename}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatFriendlyDate(r.created_at)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppWrapper>
      <Page title="Documentos Escaneados" subtitle="Lo que llega al buzón de documentos, vinculado o no">
        <BlockStack gap="400">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              load();
            }}
          >
            <TextField
              autoComplete="off"
              placeholder="Buscar por CURP, VIN, placa, nombre, RFC, archivo o remitente"
              value={search}
              onChange={setSearch}
              clearButton
              onClearButtonClick={() => setSearch("")}
            />
          </form>
          <Card padding="0">
            <Tabs
              tabs={tabs}
              selected={tab}
              onSelect={(t) => {
                setTab(t);
                setPage(1);
              }}
            />
            <IndexTable
              itemCount={rows.length}
              selectable={false}
              loading={loading}
              emptyState={
                <Box padding="400">
                  <Text tone="subdued" alignment="center">
                    No hay documentos aquí.
                  </Text>
                </Box>
              }
              headings={[
                { title: "Documento" },
                { title: "Estado" },
                { title: "Vinculado a" },
                { title: "Origen" },
                { title: "Recibido" },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
          <InlineStack align="center">
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage(page - 1)}
              hasNext={hasMore}
              onNext={() => setPage(page + 1)}
            />
          </InlineStack>
        </BlockStack>
      </Page>
      {selected && (
        <DocumentModal
          doc={selected}
          linkedLabel={linkedTo(selected)}
          mismatch={nameMismatch(selected, labels[`${selected.entity_type}:${selected.entity_id}`])}
          userId={userId}
          onClose={() => setSelected(null)}
          onChanged={() => refresh(selected.id)}
          onDeleted={async () => {
            setSelected(null);
            await refresh();
          }}
          onOpenEntity={(type, id) => navigate(entityRoute(type, id))}
        />
      )}
    </AppWrapper>
  );
}

function DocumentModal({ doc, linkedLabel, mismatch, userId, onClose, onChanged, onDeleted, onOpenEntity }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [linking, setLinking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isPdf = (doc.mime_type || "").includes("pdf") || doc.file_path.endsWith(".pdf");

  useEffect(() => {
    let alive = true;
    setUrl(null);
    signedUrl(doc).then((u) => alive && setUrl(u));
    return () => {
      alive = false;
    };
  }, [doc]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
      setLinking(false);
      setEditing(false);
    } catch (e) {
      console.error(e);
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // The model's free-text notes are kept in the row but not shown.
  const extracted = Object.entries(doc.extracted || {}).filter(
    ([k, v]) => k !== "notes" && v !== null && v !== "" && v !== true
  );

  const secondary = [];
  if (!editing) {
    secondary.push({ content: "Editar imagen", disabled: busy, onAction: () => setEditing(true) });
  }
  if (doc.original_file_path && !editing) {
    secondary.push({ content: "Restaurar original", loading: busy, onAction: () => run(() => restoreOriginal(doc, userId)) });
  }
  if (doc.status === "linked") {
    secondary.push({ content: "Desvincular", destructive: true, loading: busy, onAction: () => run(() => unlinkDocument(doc, userId)) });
  }
  if (!editing) {
    secondary.push({ content: "Eliminar", destructive: true, disabled: busy, onAction: () => setConfirmDelete(true) });
  }
  if (doc.status !== "ignored") {
    secondary.push({ content: "Ignorar", loading: busy, onAction: () => run(() => unlinkDocument(doc, userId, "ignored")) });
  } else {
    secondary.push({ content: "Restaurar", loading: busy, onAction: () => run(() => unlinkDocument(doc, userId, "unlinked")) });
  }

  return (
    <Modal
      open
      size="large"
      onClose={onClose}
      title={`${DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}${doc.summary ? ` · ${doc.summary}` : ""}`}
      primaryAction={{
        content: doc.status === "linked" ? "Cambiar vínculo" : "Vincular",
        onAction: () => setLinking(true),
        disabled: busy,
      }}
      secondaryActions={secondary}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}
          {confirmDelete && (
            <Banner
              tone="critical"
              title="¿Eliminar este documento?"
              action={{
                content: "Eliminar definitivamente",
                loading: busy,
                onAction: async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await deleteDocument(doc, userId);
                    await onDeleted();
                  } catch (e) {
                    console.error(e);
                    setError(e.message || String(e));
                    setBusy(false);
                  }
                },
              }}
              secondaryAction={{ content: "Cancelar", onAction: () => setConfirmDelete(false) }}
              onDismiss={() => setConfirmDelete(false)}
            >
              {doc.status === "linked"
                ? `Se quitará también la copia vinculada a ${linkedLabel.replace(/^[^:]+: /, "")}. No se puede deshacer.`
                : "Se borrará el archivo escaneado. No se puede deshacer."}
            </Banner>
          )}
          <InlineGrid columns={{ xs: 1, md: ["twoThirds", "oneThird"] }} gap="400">
            <Box minHeight="420px" background="bg-surface-secondary" borderRadius="200">
              {editing ? (
                <ImageEditor
                  doc={doc}
                  saving={busy}
                  onCancel={() => setEditing(false)}
                  onSave={(image, rotation, crop) => run(() => saveEdit(doc, image, rotation, crop, userId))}
                />
              ) : !url ? (
                <InlineStack align="center" blockAlign="center">
                  <Box padding="800">
                    <Spinner />
                  </Box>
                </InlineStack>
              ) : isPdf ? (
                <iframe title="documento" src={url} style={{ width: "100%", height: 560, border: 0 }} />
              ) : (
                <a href={url} target="_blank" rel="noreferrer">
                  <img alt="documento" src={url} style={{ width: "100%", borderRadius: 8 }} />
                </a>
              )}
            </Box>
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <StatusBadge status={doc.status} mismatch={mismatch} />
                {doc.status === "linked" && (
                  <Text tone="subdued" variant="bodySm">
                    {doc.linked_by ? "Manual" : `Automático · ${MATCH_LABELS[doc.match_method] || doc.match_method}`}
                  </Text>
                )}
              </InlineStack>
              {linkedLabel && (
                <InlineStack gap="200" blockAlign="center">
                  <Text fontWeight="semibold">{linkedLabel}</Text>
                  {doc.status === "linked" && entityRoute(doc.entity_type, doc.entity_id) && (
                    <Button variant="plain" onClick={() => onOpenEntity(doc.entity_type, doc.entity_id)}>
                      Abrir
                    </Button>
                  )}
                </InlineStack>
              )}
              {mismatch && (
                <Text tone="critical">El documento dice "{doc.extracted?.nombre}" — no coincide con el cliente.</Text>
              )}
              {doc.match_note && <Text tone="subdued">{doc.match_note}</Text>}
              {doc.doc_type === "recibo_icvnl" && doc.invoice_status && (
                <>
                  <Divider />
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="headingSm">Factura</Text>
                    <InvoiceBadge doc={doc} />
                  </InlineStack>
                  {doc.invoice_cfdi?.uuid && (
                    <Text variant="bodySm">
                      UUID {doc.invoice_cfdi.uuid}
                      {doc.invoice_cfdi.serie_folio ? ` · ${doc.invoice_cfdi.serie_folio}` : ""}
                    </Text>
                  )}
                  {doc.invoice_note && (
                    <Text tone="subdued" variant="bodySm">
                      {doc.invoice_note}
                    </Text>
                  )}
                  {doc.invoice_updated_at && (
                    <Text tone="subdued" variant="bodySm">
                      Actualizado {formatFriendlyDate(doc.invoice_updated_at)}
                    </Text>
                  )}
                </>
              )}
              <Divider />
              <Text variant="headingSm">Datos leídos</Text>
              {extracted.length === 0 ? (
                <Text tone="subdued">Nada legible.</Text>
              ) : (
                <BlockStack gap="100">
                  {extracted.map(([k, v]) => (
                    <InlineStack key={k} gap="200" wrap={false}>
                      <Box minWidth="110px">
                        <Text tone="subdued" variant="bodySm">
                          {FIELD_LABELS[k] || k}
                        </Text>
                      </Box>
                      <Text variant="bodySm" breakWord>
                        {v === false ? "No" : String(v)}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}
              <Divider />
              <Text tone="subdued" variant="bodySm">
                {doc.email_from || doc.source} · {doc.original_filename}
                {doc.page_index != null && ` · pág. ${doc.page_index + 1}`}
                {doc.region_index != null && ` · recorte ${doc.region_index}`}
                {doc.original_file_path && " · editado"}
              </Text>
              {doc.email_subject && (
                <Text tone="subdued" variant="bodySm">
                  Asunto: {doc.email_subject}
                </Text>
              )}
            </BlockStack>
          </InlineGrid>
        </BlockStack>
      </Modal.Section>
      {linking && (
        <Modal.Section>
          <LinkForm
            doc={doc}
            busy={busy}
            onCancel={() => setLinking(false)}
            onLink={(target) => run(() => linkDocument(doc, target, userId))}
          />
        </Modal.Section>
      )}
    </Modal>
  );
}

function LinkForm({ doc, busy, onCancel, onLink }) {
  const [entityType, setEntityType] = useState(doc.suggested_entity_type || defaultEntityType(doc));
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [choice, setChoice] = useState(null);
  const [slot, setSlot] = useState(defaultSlot(entityType, doc.doc_type));

  useEffect(() => {
    setSlot(defaultSlot(entityType, doc.doc_type));
    setChoice(null);
    setResults([]);
    // Pre-fill the search with the best identifier read from the document.
    const x = doc.extracted || {};
    setTerm(entityType === "car" ? x.placa || x.vin || "" : entityType === "company" ? x.rfc || x.razon_social || "" : x.curp || "");
  }, [entityType, doc]);

  useEffect(() => {
    if (!term.trim()) return;
    const t = setTimeout(async () => {
      setSearching(true);
      setResults(await searchEntities(entityType, term));
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [term, entityType]);

  return (
    <BlockStack gap="300">
      <Text variant="headingSm">Vincular a</Text>
      <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
        <Select
          label="Tipo"
          options={Object.entries(ENTITY_LABELS).map(([value, label]) => ({ value, label }))}
          value={entityType}
          onChange={setEntityType}
        />
        <TextField
          label="Buscar"
          autoComplete="off"
          value={term}
          onChange={setTerm}
          placeholder={entityType === "car" ? "Placa o VIN" : entityType === "company" ? "Nombre o RFC" : "Nombre o CURP"}
        />
        {entityType !== "car" && (
          <Select label="Guardar como" options={SLOTS[entityType]} value={slot} onChange={setSlot} />
        )}
      </InlineGrid>
      {searching ? (
        <Spinner size="small" />
      ) : (
        <BlockStack gap="100">
          {results.map((r) => (
            <Box
              key={r.id}
              padding="200"
              borderRadius="200"
              background={choice?.id === r.id ? "bg-surface-selected" : "bg-surface"}
              borderWidth="025"
              borderColor="border"
            >
              <div style={{ cursor: "pointer" }} onClick={() => setChoice(r)}>
                <Text fontWeight="semibold">{r.label}</Text>
                <Text tone="subdued" variant="bodySm">
                  {r.sublabel}
                </Text>
              </div>
            </Box>
          ))}
          {term.trim() && results.length === 0 && <Text tone="subdued">Sin resultados.</Text>}
        </BlockStack>
      )}
      {(entityType === "customer" || entityType === "company") && slot !== "other" && (
        <Text tone="subdued" variant="bodySm">
          Si ya hay un archivo en ese espacio, pasa a Otros documentos.
        </Text>
      )}
      <InlineStack gap="200" align="end">
        <Button onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!choice}
          loading={busy}
          onClick={() =>
            onLink({ entityType, entityId: choice.id, label: choice.label, plate: choice.plate, slot })
          }
        >
          {choice ? `Vincular a ${choice.label}` : "Elige un resultado"}
        </Button>
      </InlineStack>
    </BlockStack>
  );
}

export default ScannedDocuments;
